'use client';

import { useState, useEffect, forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useQuery } from '@tanstack/react-query';
import { CampaignCreationCardHeader } from '@/components/glitch-ui/campaigncreationcardheader';

import { LocationMapPicker } from '@/components/business/business-locations/location-map-picker';
import type { RadiusFormValue } from '@/components/business/business-locations/radius-form';
import { businessLocationsQueryOptions } from '@/lib/query-options';
import { Location, type CampaignProximityInput } from '@/lib/types';
import { motion } from 'framer-motion';

const toCampaignProximityInputs = (rs: RadiusFormValue[]): CampaignProximityInput[] =>
  rs.map((r) => ({ latitude: r.lat, longitude: r.lng, radius_km: r.radiusKm, address: r.formattedAddress }));

interface CampaignLocationsProps {
  onNext: () => void;
  setLocations: (locations: Location[]) => void;
  setStepValid: (valid: boolean) => void;
  locations?: Location[];
  businessId?: number;
  /** The business's saved locations — shown as the checklist to apply to the campaign. */
  candidates?: Location[];
  /** Single-select (Location-type campaign): radios, exactly one location. */
  singleSelect?: boolean;
  /** Opt into the Radius tab (#5444): receives the chosen radii (already in the
   * CampaignProximityInput write shape) so the wizard saves them via
   * setCampaignProximityTargets after create. Prepopulated with the business's saved
   * radius locations. Ignored for single-select campaigns. */
  setRadii?: (targets: CampaignProximityInput[]) => void;
}

export interface CampaignLocationsRef {
  approveLocations: () => boolean;
}

export const CampaignLocations = forwardRef<CampaignLocationsRef, CampaignLocationsProps>(({ setLocations: setTargetLocations, setStepValid, locations: initialLocations = [], businessId, candidates, singleSelect, setRadii }, ref) => {
  const posthog = usePostHog();
  const [locations, setLocations] = useState<Location[]>(initialLocations);
  const previousLocationsRef = useRef<Location[]>(initialLocations);
  const userEditedRef = useRef(false);

  // Radius (#5444): opt-in (setRadii provided) and multi-select only. The business's
  // saved radius locations are the checklist candidates, prepopulated/checked so the
  // new campaign owns the inherited set unless the user changes it.
  const radiusEnabled = setRadii != null && !singleSelect;
  const { data: businessLocations } = useQuery({
    ...businessLocationsQueryOptions(businessId ?? 0),
    enabled: businessId != null && radiusEnabled,
  });
  const radiusCandidates = useMemo<RadiusFormValue[]>(
    () =>
      (businessLocations ?? [])
        .filter(
          (l) =>
            l.variant === 'radius' &&
            !l.dismissed_by_user &&
            l.latitude != null &&
            l.longitude != null &&
            l.radius_km != null,
        )
        .map((l) => ({
          lat: l.latitude as number,
          lng: l.longitude as number,
          radiusKm: l.radius_km as number,
          countryCode: l.country_code ?? null,
          formattedAddress: l.radius_address ?? `${l.latitude}, ${l.longitude}`,
        })),
    [businessLocations],
  );
  const [radii, setRadiiState] = useState<RadiusFormValue[]>([]);
  const radiiSeededRef = useRef(false);
  // Seed the radii once from the business candidates (prepopulated/checked), then let
  // the user drive (mirrors the late-arriving location seed above).
  useEffect(() => {
    if (radiiSeededRef.current || !radiusEnabled) return;
    if (radiusCandidates.length > 0) {
      setRadiiState(radiusCandidates);
      setRadii?.(toCampaignProximityInputs(radiusCandidates));
      radiiSeededRef.current = true;
    }
  }, [radiusCandidates, radiusEnabled]);
  const handleRadiiChange = (next: RadiusFormValue[]) => {
    setRadiiState(next);
    setRadii?.(toCampaignProximityInputs(next));
  };

  // Adopt a late-arriving seed (#4794). Every wizard seeds `locations` from the business
  // query, which resolves after this component mounts — so a mount-only useState copy
  // silently drops the seed. Adopt the prop while the picker is empty and untouched;
  // after the user's first edit the prop is ignored so a deliberate clear isn't clobbered.
  useEffect(() => {
    if (userEditedRef.current) return;
    if (locations.length === 0 && initialLocations.length > 0) {
      setLocations(initialLocations);
      previousLocationsRef.current = initialLocations;
    }
  }, [initialLocations]);

  const handleChange = (newLocations: Location[]) => {
    userEditedRef.current = true;
    const previousLocations = previousLocationsRef.current;

    // Detect if location was added or removed
    if (newLocations.length > previousLocations.length) {
      // Location added
      const addedLocation = newLocations.find(
        (loc) => !previousLocations.some((prev) => prev.criteria_id === loc.criteria_id)
      );

      if (addedLocation) {
        posthog?.capture('onboarding_location_selected', {
          location_id: addedLocation.criteria_id,
          location_name: addedLocation.name,
          location_country_code: addedLocation.country_code,
          total_locations: newLocations.length,
        });
      }
    } else if (newLocations.length < previousLocations.length) {
      // Location removed
      const removedLocation = previousLocations.find(
        (loc) => !newLocations.some((current) => current.criteria_id === loc.criteria_id)
      );

      if (removedLocation) {
        posthog?.capture('onboarding_location_removed', {
          location_id: removedLocation.criteria_id,
          location_name: removedLocation.name,
          location_country_code: removedLocation.country_code,
          remaining_locations: newLocations.length,
        });
      }
    }

    setLocations(newLocations);
    setTargetLocations(newLocations);
    previousLocationsRef.current = newLocations;
  };

  const approveLocations = (): boolean => {
    setTargetLocations(locations);

    posthog?.capture('onboarding_locations_approved', {
      location_count: locations.length,
      location_types: locations.map((loc) => loc.target_type),
      location_names: locations.map((loc) => loc.name),
    });

    return true;
  };

  useImperativeHandle(ref, () => ({
    approveLocations
  }));

  // Valid when there's at least one geo location OR (radius-enabled wizards) at least
  // one radius — a radius-only campaign is a legitimate targeting choice (#5444).
  useEffect(() => {
    setStepValid(locations.length > 0 || radii.length > 0);
  }, [locations, radii]);

  return (
    <motion.div
      className='space-y-4'
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
    >
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <CampaignCreationCardHeader
          title=''
          subTitle='Search for the locations where you want your ads to appear. You can add countries, regions, states, or cities.'
        />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        {/* #5518: shorter, viewport-responsive map so the wizard's Back/Next stay on-screen. */}
        <div className="h-[clamp(320px,48vh,480px)] overflow-hidden rounded-lg border border-gray-200">
          {businessId != null ? (
            <LocationMapPicker
              businessId={businessId}
              selected={locations}
              onChange={handleChange}
              catalogOnly
              candidates={candidates ?? []}
              radiusCandidates={radiusEnabled ? radiusCandidates : undefined}
              radii={radii}
              onRadiiChange={handleRadiiChange}
              singleSelect={singleSelect}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">Loading…</div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
});
